import React, { useState } from "react";
import {
  FiCpu,
  FiPlay,
  FiPlus,
  FiClock,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
  FiTrash2,
  FiEdit2,
  FiLayers,
  FiRefreshCw,
} from "react-icons/fi";
import { MdAutoAwesome } from "react-icons/md";
import useAutomations, {
  PROGNOS_TEMPLATES,
} from "hooks/automations/useAutomations";
import AutomationEditorModal from "./AutomationEditorModal";
import ExecutionLogsModal from "./ExecutionLogsModal";

const AutomationsPage = () => {
  const {
    automations,
    runs,
    isLoading,
    isExecuting,
    isGenerating,
    stats,
    triggerRun,
    toggleStatus,
    removeAutomation,
    saveAutomation,
    buildWithAI,
    createFromTemplate,
  } = useAutomations();

  const [aiPrompt, setAiPrompt] = useState("");
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);

  const handleOpenCreateModal = () => {
    setEditingAutomation(null);
    setEditorModalOpen(true);
  };

  const handleOpenEditModal = (auto) => {
    setEditingAutomation(auto);
    setEditorModalOpen(true);
  };

  const handleViewLogs = (run) => {
    setSelectedRun(run);
    setLogsModalOpen(true);
  };

  const handleViewLatestRunForAutomation = (automationId) => {
    const run = runs.find((r) => r.automationId === automationId);
    if (run) {
      setSelectedRun(run);
      setLogsModalOpen(true);
    }
  };

  const handleAiBuildSubmit = async (e) => {
    e.preventDefault();
    if (!aiPrompt.trim()) return;
    const result = await buildWithAI(aiPrompt);
    if (result) {
      setAiPrompt("");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/70 text-slate-800 p-6 md:p-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="flex items-center gap-3">
            <span className="p-2.5 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-200 shadow-sm">
              <FiCpu size={26} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black tracking-tight text-slate-900">
                  Ավտոմատացում և Որոնման Գրաֆիկ
                </h1>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">
                  AI Agents
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Համացանցից կանխատեսումների, պաշտոնական խոստումների և արդիական
                բովանդակության ավտոմատացված հավաքագրում
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md shadow-indigo-200 transition transform active:scale-95"
          >
            <FiPlus size={18} />
            Նոր ավտոմատացում
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            label: "Բոլոր առաջադրանքները",
            value: stats.totalAutomations,
            desc: "Կազմաձևված ավտոմատացումներ",
            color: "text-indigo-600",
            bg: "bg-white border-slate-200 shadow-sm",
          },
          {
            label: "Ակտիվ գրաֆիկներ",
            value: stats.activeAutomations,
            desc: "Ավտոմատ աշխատող ծրագրեր",
            color: "text-emerald-600",
            bg: "bg-white border-slate-200 shadow-sm",
          },
          {
            label: "Հայտնաբերված նյութեր",
            value: stats.totalFindings,
            desc: "Կանխատեսումներ և խոստումներ",
            color: "text-amber-600",
            bg: "bg-white border-slate-200 shadow-sm",
          },
          {
            label: "Հաջողության մակարդակ",
            value: stats.successRate,
            desc: "Անխափան գործարկումներ",
            color: "text-purple-600",
            bg: "bg-white border-slate-200 shadow-sm",
          },
        ].map((st, i) => (
          <div key={i} className={`p-4 rounded-2xl border ${st.bg} space-y-1`}>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
              {st.label}
            </span>
            <div className={`text-2xl font-black ${st.color}`}>{st.value}</div>
            <p className="text-[11px] text-slate-400">{st.desc}</p>
          </div>
        ))}
      </div>

      {/* AI Assistant Generator Bar */}
      <div className="p-5 rounded-2xl bg-gradient-to-r from-indigo-50/90 via-white to-purple-50/90 border border-indigo-200/80 shadow-sm relative overflow-hidden">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1 max-w-xl">
            <div className="flex items-center gap-2 text-indigo-700 font-bold text-sm">
              <MdAutoAwesome className="text-indigo-600" />
              <span>Ստեղծել նոր ավտոմատացում AI հրահանգով</span>
            </div>
            <p className="text-xs text-slate-600">
              Նկարագրեք ձեր նպատակը, և Gemini AI-ն ինքնուրույն կկազմի որոնման
              բանալի բառերը, լրատվականների ցանկը և գրաֆիկը:
            </p>
          </div>

          <form
            onSubmit={handleAiBuildSubmit}
            className="w-full md:w-auto flex-1 flex gap-2 max-w-2xl"
          >
            <input
              type="text"
              placeholder="օր. «Ամեն օր գտնել Երևանի քաղաքապետարանի նոր խոստումները տրանսպորտի մասին»"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="flex-1 bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition shadow-sm"
            />
            <button
              type="submit"
              disabled={isGenerating || !aiPrompt.trim()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white transition shrink-0 shadow-sm"
            >
              {isGenerating ? (
                <>
                  <FiRefreshCw className="animate-spin" />
                  <span>Կառուցում է...</span>
                </>
              ) : (
                <>
                  <MdAutoAwesome />
                  <span>Կառուցել AI-ով</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Pre-built Prognos Templates */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
            <FiLayers className="text-indigo-600" />
            Պատրաստի Prognos շաբլոններ (Templates)
          </h2>
          <span className="text-xs text-slate-500">
            Արագ ակտիվացրեք նախապես կոնֆիգուրացված լրահոսի սկաներները
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROGNOS_TEMPLATES.map((tmpl) => (
            <div
              key={tmpl.id}
              className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col justify-between hover:border-indigo-300 hover:shadow-md transition group"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {tmpl.trigger.schedule.interval === "day"
                      ? "Օրական"
                      : "12 ժամը մեկ"}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {tmpl.targetEntities.join(" + ")}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition">
                  {tmpl.name}
                </h3>
                <p className="text-xs text-slate-500 line-clamp-3">
                  {tmpl.description}
                </p>
              </div>

              <button
                onClick={() => createFromTemplate(tmpl)}
                className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-indigo-600 text-slate-700 hover:text-white transition"
              >
                <FiPlus size={14} /> Օգտագործել շաբլոնը
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Configured Automations Table */}
      <div className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
          Կազմաձևված ավտոմատացումներ ({automations.length})
        </h2>

        {isLoading ? (
          <div className="text-center py-12 text-slate-500">
            <FiRefreshCw className="animate-spin mx-auto mb-2" size={24} />
            <p>Բեռնվում են ավտոմատացումները...</p>
          </div>
        ) : automations.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <FiCpu size={40} className="mx-auto text-slate-400 mb-3" />
            <h3 className="text-base font-bold text-slate-900 mb-1">
              Դեռևս ավտոմատացումներ չկան
            </h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto mb-4">
              Սեղմեք «Նոր ավտոմատացում» կամ ընտրեք վերևի պատրաստի շաբլոններից
              մեկը՝ լրահոսից նոր կանխատեսումներ և խոստումներ հավաքելու համար:
            </p>
            <button
              onClick={handleOpenCreateModal}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white transition shadow"
            >
              Ստեղծել առաջին ավտոմատացումը
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider border-b border-slate-200 text-[11px]">
                  <tr>
                    <th className="py-3.5 px-4 font-bold">Անվանում և թիրախ</th>
                    <th className="py-3.5 px-4 font-bold">Գրաֆիկ</th>
                    <th className="py-3.5 px-4 font-bold">Կարգավիճակ</th>
                    <th className="py-3.5 px-4 font-bold">Վերջին գործարկում</th>
                    <th className="py-3.5 px-4 text-right font-bold">Գործողություններ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {automations.map((auto) => {
                    const isRunning = isExecuting === auto.id;
                    return (
                      <tr
                        key={auto.id}
                        className="hover:bg-slate-50/80 transition"
                      >
                        {/* Name & Target Entities */}
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-sm text-slate-900 mb-1">
                            {auto.name}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {(auto.targetEntities || []).map((ent) => (
                              <span
                                key={ent}
                                className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200"
                              >
                                {ent === "predictions"
                                  ? "Կանխատեսումներ"
                                  : ent === "promises"
                                  ? "Խոստումներ"
                                  : "Հարցումներ"}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* Schedule */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1.5 text-slate-700 font-medium">
                            <FiClock className="text-indigo-600" />
                            {auto.trigger?.type === "schedule" ? (
                              auto.trigger.schedule?.interval === "hour" ? (
                                "Ամեն 1 ժամը մեկ"
                              ) : auto.trigger.schedule?.interval ===
                                "6hours" ? (
                                "Ամեն 6 ժամը մեկ"
                              ) : auto.trigger.schedule?.interval ===
                                "12hours" ? (
                                "Ամեն 12 ժամը մեկ"
                              ) : (
                                "Օրական 1 անգամ"
                              )
                            ) : (
                              <span className="text-slate-400">Ձեռքով</span>
                            )}
                          </div>
                        </td>

                        {/* Status Toggle */}
                        <td className="py-3.5 px-4">
                          <button
                            onClick={() => toggleStatus(auto)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
                              auto.status === "published"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                            }`}
                          >
                            {auto.status === "published"
                              ? "● Ակտիվ"
                              : "○ Կասեցված"}
                          </button>
                        </td>

                        {/* Last Run Info */}
                        <td className="py-3.5 px-4">
                          {auto.lastRunAt ? (
                            <div>
                              <div className="flex items-center gap-1.5 text-xs text-slate-700">
                                {auto.lastRunStatus === "success" ? (
                                  <FiCheckCircle className="text-emerald-600" />
                                ) : (
                                  <FiAlertCircle className="text-rose-600" />
                                )}
                                <span>
                                  {auto.lastRunAt?.toDate
                                    ? auto.lastRunAt
                                        .toDate()
                                        .toLocaleDateString("hy-AM", {
                                          month: "short",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                    : "Վերջերս"}
                                </span>
                              </div>
                              {auto.lastRunFindingsCount !== undefined && (
                                <button
                                  onClick={() =>
                                    handleViewLatestRunForAutomation(auto.id)
                                  }
                                  className="text-[11px] text-indigo-600 hover:underline font-semibold mt-0.5 block"
                                >
                                  +{auto.lastRunFindingsCount} նյութ
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs">
                              Դեռ չի գործարկվել
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => triggerRun(auto.id)}
                              disabled={isRunning}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white transition shadow-sm"
                              title="Գործարկել հիմա"
                            >
                              {isRunning ? (
                                <FiRefreshCw
                                  className="animate-spin"
                                  size={13}
                                />
                              ) : (
                                <FiPlay size={13} />
                              )}
                              <span>{isRunning ? "Որոնում է..." : "Գործարկել"}</span>
                            </button>

                            <button
                              onClick={() => handleOpenEditModal(auto)}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
                              title="Խմբագրել"
                            >
                              <FiEdit2 size={15} />
                            </button>

                            <button
                              onClick={() => removeAutomation(auto.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                              title="Հեռացնել"
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Recent Execution Runs History */}
      {runs.length > 0 && (
        <div className="space-y-3 pt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
              <FiFileText className="text-indigo-600" />
              Վերջին գործարկումների պատմություն ({runs.length})
            </h2>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider border-b border-slate-200 text-[11px]">
                  <tr>
                    <th className="py-3 px-4 font-bold">Առաջադրանք</th>
                    <th className="py-3 px-4 font-bold">Գործարկման ժամ</th>
                    <th className="py-3 px-4 font-bold">Կարգավիճակ</th>
                    <th className="py-3 px-4 font-bold">Գտնված նյութեր</th>
                    <th className="py-3 px-4 text-right font-bold">Մանրամասն</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {runs.slice(0, 10).map((r) => (
                    <tr
                      key={r.id}
                      className="hover:bg-slate-50/80 transition cursor-pointer"
                      onClick={() => handleViewLogs(r)}
                    >
                      <td className="py-3 px-4 font-bold text-slate-900">
                        {r.automationName || "Prognos Scanner"}
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {r.startedAt?.toDate
                          ? r.startedAt.toDate().toLocaleString("hy-AM")
                          : "Նոր"}
                      </td>
                      <td className="py-3 px-4">
                        {r.status === "success" ? (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Հաջողված
                          </span>
                        ) : r.status === "failed" ? (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                            Ձախողված
                          </span>
                        ) : (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            Ընթացքի մեջ
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-bold text-indigo-600">
                        {r.findingsCount || 0} նյութ
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLogs(r);
                          }}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold"
                        >
                          Դիտել լոգերը →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AutomationEditorModal
        isOpen={editorModalOpen}
        onClose={() => setEditorModalOpen(false)}
        automation={editingAutomation}
        onSave={saveAutomation}
      />

      <ExecutionLogsModal
        isOpen={logsModalOpen}
        onClose={() => setLogsModalOpen(false)}
        run={selectedRun}
      />
    </div>
  );
};

export default AutomationsPage;
